import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import Docs from '/src/_includes/graphql/catalog-service/headers.md';
import CustomerGroupCode from '/src/_includes/graphql/customer-group-code.md';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "recommendations-query"
    }}>{`recommendations query`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`recommendations`}</inlineCode>{` query returns information about product recommendation blocks for a given SKU.`}</p>
    <p>{`Merchants must have both Product Recommendations and Catalog Service (v2.2.0+) installed to get complete data through the storefront gateway.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The `}<inlineCode parentName="p">{`recommendations`}</inlineCode>{` query does not support the `}<inlineCode parentName="p">{`alternateEnvironmentId`}</inlineCode>{` attribute.`}</p>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/developer/commerce/storefront/playgrounds/commerce-services/"
      }}>{`Commerce API playground`}</a>{` provides a sample `}<inlineCode parentName="p">{`recommendations`}</inlineCode>{` query that you can run against a live instance of Adobe Commerce with Luma sample data. Note that the responses may vary, depending on the configuration of the Commerce instance.`}</p>
    <h2 {...{
      "id": "required-headers"
    }}>{`Required headers`}</h2>
    <p>{`You must specify the following HTTP headers to run this query.`}</p>

    <Docs mdxType="Docs" />
    <h3 {...{
      "id": "find-the-customer-group-code"
    }}>{`Find the customer group code`}</h3>

    <CustomerGroupCode mdxType="CustomerGroupCode" />
    <h2 {...{
      "id": "syntax"
    }}>{`Syntax`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`recommendations(
    currentSku: String,
    userPurchaseHistory: [PurchaseHistory],
    userViewHistory: [ViewHistory],
    cartSkus: [String],
    category: String,
    pageType: PageType
): Recommendations
`}</code></pre>
    <h2 {...{
      "id": "example-usage"
    }}>{`Example usage`}</h2>
    <p>{`The following query returns product recommendations for SKU `}<inlineCode parentName="p">{`MP01`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Request:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-graphql"
      }}>{`{
recommendations(currentSku: "MP01", pageType: Product) {
    totalResults
    results {
        unitId
        unitName
        totalProducts
        pageType
        typeId
        storefrontLabel
        displayOrder
        productsView {
            sku
            rank
            score
            queryType
            categories
            visibility
        }
    }
}
}
`}</code></pre>
    <p><strong parentName="p">{`Response:`}</strong></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"data": {
    "recommendations": {
        "totalResults": 1,
        "results": [
            {
                "unitId": "6401de2a-7499-4648-847c-b1d62f6ee947",
                "unitName": "PDMLT",
                "totalProducts": 5,
                "pageType": "Product",
                "typeId": "more-like-this",
                "storefrontLabel": "PDMLT",
                "displayOrder": 2,
                "productsView": [
                    {
                        "sku": "MP08",
                        "rank": 1,
                        "score": 80.98603,
                        "queryType": "primary",
                        "categories": [
                            "collections/yoga-new",
                            "men/bottoms-men",
                            "men/bottoms-men/pants-men",
                            "promotions/pants-all",
                            "sale"
                        ],
                        "visibility": "Catalog, Search"
                    },
                    {
                        "sku": "WP05",
                        "rank": 2,
                        "score": 47.418564,
                        "queryType": "primary",
                        "categories": [
                            "women/bottoms-women",
                            "women/bottoms-women/pants-women",
                            "promotions/pants-all",
                            "collections/erin-recommends"
                        ],
                        "visibility": "Catalog, Search"
                    },
                    {
                        "sku": "WP04",
                        "rank": 3,
                        "score": 46.906868,
                        "queryType": "primary",
                        "categories": [
                            "women/bottoms-women",
                            "women/bottoms-women/pants-women",
                            "promotions/pants-all"
                        ],
                        "visibility": "Catalog, Search"
                    },
                    {
                        "sku": "WP02",
                        "rank": 4,
                        "score": 44.674534,
                        "queryType": "primary",
                        "categories": [
                            "women/bottoms-women",
                            "women/bottoms-women/pants-women",
                            "promotions/pants-all",
                            "collections/performance-fabrics"
                        ],
                        "visibility": "Catalog, Search"
                    },
                    {
                        "sku": "WP01",
                        "rank": 5,
                        "score": 41.731136,
                        "queryType": "primary",
                        "categories": [
                            "women/bottoms-women",
                            "women/bottoms-women/pants-women",
                            "promotions/pants-all",
                            "collections/performance-fabrics"
                        ],
                        "visibility": "Catalog, Search"
                    }
                ]
            }
        ]
    }
}
`}</code></pre>
    <h2 {...{
      "id": "input-fields"
    }}>{`Input fields`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cartSKUs`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[String]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SKUs of the products in the cart.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`category`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The category currently being viewed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`currentSKU`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SKU of the product currently being viewed on the product detail page.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`pageType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`PageType`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`An enum indicating the type of page on which recommendations are requested. Possible values are Cart, Category, Checkout, CMS, PageBuilder and Product.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`userPurchaseHistory`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[PurchaseHistory]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User purchase history with timestamp.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`userViewHistory`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[ViewHistory]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`User view history with timestamp.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "purchasedhistory"
    }}>{`PurchasedHistory`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`date`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DateTime`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Date of purchase`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`items`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[String]`}{`!`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A list of items purchased`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "viewhistory"
    }}>{`ViewHistory`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Field`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`date`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`DateTime`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The time the items were viewed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sku`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[String]`}{`!`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`A list of SKUs viewed`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "output-fields"
    }}>{`Output fields`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`Recommendations`}</inlineCode>{` object contains details about recommended products for a given SKU. It contains the following attributes.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Attribute`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`results`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[RecommendationUnit]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Array of recommendation units for recommended products`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`totalResults`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Total number of recommendation units for returned recommendation`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "recommendationunit-attributes"
    }}>{`RecommendationUnit attributes`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Attribute`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`displayOrder`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Order in which recommendation units are displayed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`pageType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Page Type`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`productsView`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[ProductView]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List of product view`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`storefrontLabel`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Storefront label to be displayed on the storefront`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`totalProducts`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Total number of products returned for a recommendation`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`typeId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Type of recommendation`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`unitId`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Id of the preconfigured unit`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`unitName`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Name of the preconfigured unit`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "productview-interface"
    }}>{`ProductView interface`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`ProductView`}</inlineCode>{` return object is an interface that can contain the following fields. It is implemented by the `}<inlineCode parentName="p">{`SimpleProductView`}</inlineCode>{` and `}<inlineCode parentName="p">{`ComplexProductView`}</inlineCode>{` types. Both these types contain the same set of fields as `}<inlineCode parentName="p">{`ProductView`}</inlineCode>{`.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Attribute`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Data Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`categories`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`[String]`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List of categories to which the product belongs`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`rank`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Int`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Rank given to a product`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`queryType`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Indicates if the product was retrieved from the primary or backup query`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`score`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Float`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Score indicating relevance of the product to the recommendation type`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sku`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String!`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The product SKU`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`visibility`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Visibility setting of the product`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      